import { Component, Input, ViewChildren, QueryList, ChangeDetectorRef } from '@angular/core';
import { CrewAssignmentService, UserService } from '#services/api';
import { CrewAssignment, User } from '#models/index';
import { SelectItemGroup, MessageService, ConfirmationService } from 'primeng/api';
import { LoadingIndicatorService } from '#services/shared';
import { TranslateService } from '@ngx-translate/core';
import { finalize, firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-assignment-grid',
    templateUrl: './assignmentgrid.component.html',
    styleUrls: ['./assignmentgrid.component.scss'],
    standalone: false
})
export class AssignmentGridComponent  {
  @ViewChildren('dv') dataView: QueryList<any>;

  public _assignments: any[] = [];
  private updateList: CrewAssignment[];
  selectedCrew = [];
  selectedCrewUser = [];
  unassignDialogVisible: boolean;
  Fullname: string;
  @Input()
  set assignments(assignments: CrewAssignment[]) {
    this._assignments = assignments ? assignments.filter(a => !a.IsDeleted) : [];
    this.showPaginator = this._assignments.length > this.pageSize;

    if (this.dataView?.first) {
      this.dataView.first.first = 0;
    }
  }
  get assignments(): CrewAssignment[] {
    return this._assignments;
  }

  @Input()
  crews: SelectItemGroup[];

  @Input()
  projectID: number;

  @Input()
  isProjectCompleted: boolean;


  showPaginator = false;
  pageSize = 50;

  filterValue = '';
  newUser: User;
  userList: User[];
  sortField = 'User.FirstName';
  sortOrder = 1;

  constructor(
    private messageSvc: MessageService,
    private confirmationSvc: ConfirmationService,
    private loadingIndicatorSvc: LoadingIndicatorService,
    private assignmentSvc: CrewAssignmentService,
    private userSvc: UserService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}



  private setEditMode(a: any, mode: boolean): void {
    if (mode === true) {
      a.backup = {
        ProjectId: a.ProjectId,
        IsDeleted: true,
        UserId: a.UserId,
        Crew: a.Crew,
        IsReadOnly: a.IsReadOnly
      };
    } else {
      a.backup = null;
    }
  }

  addUser(event, dataView) {
    this.filterValue = '';
    dataView.filter(this.filterValue);
    const newOne = this.assignmentSvc.createAssigntmentModel(event);
    if (this.crews.length > 0) {
      newOne.Crew = this.crews[0].items[0].value;
      newOne.ProjectId = this.projectID;
      newOne.IsDeleted = false;
      newOne.newItem = true;
      newOne.IsReadOnly = false;
    }

    this.setEditMode(newOne, true);

    this.assignments.unshift(newOne);

    this.newUser = null;
  }

  searchUsers(event) {
    this.userSvc.search(event.query).subscribe({
      next:(data) => {
        this.userList = data;
        this.cdr.detectChanges();
      },
      error:(e) => {
        this.userList = [];
        this.cdr.detectChanges();
      }
    });
  }

  async validateSave(a: any) : Promise<boolean> {
    const isValid = !this._assignments
      .filter(e => e !== a)
      .some(e =>
        e.UserId === a.UserId
        // If other assignments are editable compare against backup, otherwise compare against normal Crew
        && ((e.backup && (e.backup.Crew.CrewId === a.Crew.Crewid)) || (e.Crew.CrewId === a.Crew.CrewId)));
    if(!isValid) {
      this.messageSvc.add({
        severity: 'error',
        summary: await firstValueFrom(this.translate.get('COMPONENTS.ValidationError')),
        detail: await firstValueFrom(this.translate.get('COMPONENTS.UserAssigned')),
        sticky: true,
      });
    }
    else{
      this.messageSvc.add({ severity: 'success', summary: 'Success', detail: 'Crew Saved...' });
    }
    return isValid;
  }

 async saveAssignment(a: any): Promise<void> {
    const isValidate = await this.validateSave(a);
    if(!isValidate || this.isProjectCompleted) {
      return;
    }
    else {
    const updates = [a];
    if(!a.newItem && a.backup.Crew.CrewId !== a.Crew.CrewId) {
      a.backup.IsDeleted = true;
      updates.push(a.backup);
    }

    this.loadingIndicatorSvc.show();
    this.assignmentSvc
      .updateAssignment(updates).pipe(
      finalize(() => this.loadingIndicatorSvc.hide()))
      .subscribe(
        data => {
          this.setEditMode(a, false);
          a.newItem = false;
          this.assignments.unshift(a);
          this.assignments = [...this.assignments]; // Reassign to trigger change detection
          this.cdr.detectChanges();
        }
      );
    }
  }

  updateUserViewOnlyAccess(userAssignment: any): void {
    this.updateList = [userAssignment];
    this.confirmationSvc.confirm({
      header: this.translate.instant('USERS.PleaseConfirm'),
      message: (userAssignment.IsReadOnly ? this.translate.instant('USERS.TurnOnAccess') : this.translate.instant('USERS.TurnOffAccess')) + `${userAssignment.User.FirstName + ' ' + userAssignment.User.LastName}?`,
      acceptIcon: 'pi pi-check mr-2',
      rejectIcon: 'pi pi-times mr-2',
      rejectButtonStyleClass: 'p-button-sm',
      acceptButtonStyleClass: 'p-button-outlined p-button-sm',
      accept: () => {
        let backup = JSON.parse(JSON.stringify(userAssignment));
        this.loadingIndicatorSvc.show();
          backup.IsReadOnly = !userAssignment.IsReadOnly;
          backup.IsDeleted = true;
          this.updateList.push(backup);
        this.assignmentSvc
        .updateAssignment(this.updateList).pipe(
          finalize(() => this.loadingIndicatorSvc.hide()))
          .subscribe(
            (data: any) => {
            });
      },
      reject: () => {
        userAssignment.IsReadOnly = !userAssignment.IsReadOnly;
      }
    });
  }

  enterEditMode(a: any): void {
    if (!a || !a.User || !a.User.Id) {
      console.error('Invalid user data passed to enterEditMode:', a);
      return;
    }
  
    this.Fullname = a.User.FirstName + ' ' + a.User.LastName;
    this.uemail = a.User.Email;
  
    // Initialize `this.event` with the selected user's data
    this.event = {
      value: a.User // Set the user object as the value
    };
  
    // Select all crew for the current user
    this.selectedCrewUser = this.getAssignmentsByEmail(this.uemail).map(assignment => ({
      label: assignment.Crew.Name,
      value: assignment.Crew
    }));
  
    this.unassignDialogVisible = true;
  }

getAssignmentsByEmail(email: string): CrewAssignment[] {
  return this.assignments.filter(assignment => assignment.User.Email === email);
}


  exitEditMode(a: any): void {
    if(this.isProjectCompleted) {
      return;
    } else {
      if (!a.Crew) {
        const index = this.assignments.indexOf(a);
        if (index >= 0) {
          this.assignments.splice(index, 1);
        }
      } else {
        a.Crew = a.backup.Crew;
        this.setEditMode(a, false);
      }
    }
  }

  async deleteAssignment(a: CrewAssignment, dataView, showConfirmation: boolean = true): Promise<void> {
    if (this.isProjectCompleted) {
      return;
    }
  
    if (showConfirmation) {
      // Show confirmation dialog if `showConfirmation` is true
      this.confirmationSvc.confirm({
        header: await firstValueFrom(this.translate.get('COMPONENTS.PleaseConfirm')),
        message: await firstValueFrom(this.translate.get('COMPONENTS.RemoveAssignment', { firstName: a.User.FirstName, lastName: a.User.LastName })),
        icon: 'pi pi-question-circle',
        acceptIcon: 'pi pi-check mr-2',
        rejectIcon: 'pi pi-times mr-2',
        rejectButtonStyleClass: 'p-button-sm',
        acceptButtonStyleClass: 'p-button-outlined p-button-sm',
        accept: () => {
          this.performDelete(a, dataView,showConfirmation);
        }
      });
    } else {
      // Directly delete the assignment without confirmation
      await this.performDelete(a, dataView,showConfirmation);
    }
  }
  
  private async performDelete(a: CrewAssignment, dataView, showConfirmation: boolean): Promise<void> {
    this.loadingIndicatorSvc.show();
    a.IsDeleted = true;
    this.assignmentSvc
      .updateAssignment([a])
      .pipe(finalize(() => this.loadingIndicatorSvc.hide()))
      .subscribe(() => {
        this.filterValue = '';
        dataView.filter(this.filterValue);
        const index = this.assignments.indexOf(a);
        if (index >= 0) {
          this.assignments.splice(index, 1);
          this.assignments = [...this.assignments]; // Trigger change detection
        }
         // Add success message after successful deletion
         if (showConfirmation) {
           this.messageSvc.add({ severity: 'info', summary: 'Success', detail: 'Crew Deleted...' });
         }
    
         this.cdr.detectChanges();
      });
  }

  sort(sortField: string): void {
    if(sortField === this.sortField) {
      this.sortOrder = this.sortOrder * -1;
    } else {
      this.sortField = sortField;
    }
  }
visible: boolean = false;
event
fname
lname
uemail
  addUsers(event){
    this.event = event.value;
    this.fname = event.value.FirstName;
    this.lname = event.value.LastName;
    this.uemail = event.value.Email;
    this.visible = true;
    this.newUser = null;
  }

  saveCrew(dataView){
    if(this.selectedCrew.length > 0){
      this.filterValue = '';
      dataView.filter(this.filterValue);
      for(let i of this.selectedCrew){
        let newOne = this.assignmentSvc.createAssigntmentModel(this.event);
        newOne.Crew = i.value;
        newOne.ProjectId = this.projectID;
        newOne.IsDeleted = false;
        newOne.newItem = true;
        newOne.IsReadOnly = false;
        this.saveAssignment(newOne);
      }
         this.visible = false;
         this.selectedCrew = [];
    }
    else{
          this.messageSvc.add({ severity: 'error', summary: 'Error', detail: 'No Crew Selected...' });
    }
 }

 SaveUnassignCrew(dataView): void {

  if (!this.event || !this.event.value || !this.event.value.Id) {
    console.error('Event is not properly initialized. Cannot proceed with SaveUnassignCrew.', this.event);
    return;
  }

  if (this.selectedCrewUser.length === 0) {
    // No crews selected, delete all assignments for the user and close the modal

    // Get all assignments for the user
    const userAssignments = this.getAssignmentsByEmail(this.uemail);

    // Delete all assignments for the user
    for (const assignment of userAssignments) {
      assignment.IsDeleted = true; // Mark the assignment as deleted
      this.deleteAssignment(assignment, dataView, false); // Skip confirmation dialog
    }

    // Close the modal
    this.unassignDialogVisible = false;
    return; // Exit the method since no data is selected
  }

  // If crews are selected, proceed with the existing flow
  this.filterValue = '';
  dataView.filter(this.filterValue); // Clear any filters applied to the data view

  for (let i of this.selectedCrewUser) {
    // Create a new assignment model for the selected crew
    let newOne = this.assignmentSvc.createAssigntmentModel(this.event.value);
    newOne.Crew = i.value; // Assign the crew object
    newOne.ProjectId = this.projectID; // Assign the project ID
    newOne.IsDeleted = false; // Mark the assignment as active
    newOne.newItem = true; // Mark it as a new item
    newOne.IsReadOnly = false; // Allow editing of the assignment


    // Save the unassignment without validating duplicates
    this.saveUnassignment(newOne);
  }

  // Close the dialog and reset the selected crew list
  this.unassignDialogVisible = false;
  this.selectedCrewUser = [];
    // Show update message when updated through modal
  this.messageSvc.add({ severity: 'info', summary: 'Success', detail: 'Crew Updated...' });
  
}

async saveUnassignment(a: any): Promise<void> {
  if (this.isProjectCompleted) {
    return;
  }

  // Use `this.uemail` if available, otherwise fallback to `a.User.Email`
  const email = this.uemail || (a && a.User && a.User.Email);

  // Validate the email
  if (!email) {
    console.error('Email is not defined. Cannot proceed with saveUnassignment.', { uemail: this.uemail, user: a });
    return; // Exit the method if the email is invalid
  }

  // Step 1: Delete all records of the user
  const userAssignments = this.getAssignmentsByEmail(email); // Use the validated email

  for (const assignment of userAssignments) {
    assignment.IsDeleted = true; // Mark the assignment as deleted
    await this.deleteAssignment(assignment, this.dataView?.first, false); // Skip confirmation dialog
  }

  // Step 2: Prepare the updates array for updating the assignment
  const updates = [a];

  // If the assignment is not new and the crew has changed, mark the old assignment as deleted
  if (!a.newItem && a.backup?.Crew?.CrewId !== a.Crew.CrewId) {
    a.backup.IsDeleted = true;
    updates.push(a.backup);
  }

  // Step 3: Call updateAssignment to save the updated assignment
  this.loadingIndicatorSvc.show();
  this.assignmentSvc
    .updateAssignment(updates)
    .pipe(finalize(() => this.loadingIndicatorSvc.hide())) // Hide the loading indicator after the update
    .subscribe({
      next: (data) => {
        // After a successful update, reset the edit mode and update the assignment list
        this.setEditMode(a, false);
        a.newItem = false;
        this.assignments.unshift(a); // Add the updated assignment to the list
        this.assignments = [...this.assignments]; // Trigger change detection
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error saving unassignment:', err); // Log any errors
      },
    });
}
}
